import AdminArt from '@/assets/side.webp'
import ProjectArt from '@/assets/side2.webp'
import { Panel, PanelStatus } from '@/components/layouts/panels/Panel'
import { isRoute, RouteNames } from '@/routes'
import { cn } from '@repo/ui/utils'
import { PanelRightOpen } from 'lucide-react'
import { memo, ReactNode, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

const rightSize = 600
export const mainRightOffset = 50
const rightOffset = rightSize - mainRightOffset

export const RightPanel = memo(function ({
  children,
  className,
  status,
}: {
  children?: ReactNode
  className?: string
  status: PanelStatus
}) {
  if (status === 'floating') return <AnimatedPanel>{children}</AnimatedPanel>
  if (status === 'fixed')
    return (
      <div
        className={cn(
          'relative my-4 flex flex-1 2xl:my-6',
          'mr-4 2xl:mr-6',
          `max-w-[480px] 2xl:max-w-[600px]`,
          className,
        )}
      >
        <PanelContent className="rounded-lg">{children}</PanelContent>
      </div>
    )
  return null
})

function AnimatedPanel({ children }: { children?: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  useEffect(() => {
    setIsOpen(false)
  }, [])
  const closePanel = () => setIsOpen(false)
  return (
    <Panel
      offset={rightOffset}
      width={rightSize}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className="right-0"
    >
      <PanelContent
        leftSlot={!isOpen && <RightPanelIcon closePanel={closePanel} />}
        className="rounded-l-lg"
        onTouchMove={closePanel}
      >
        <div className={cn(isOpen ? 'block' : 'hidden')}>{children}</div>
      </PanelContent>
    </Panel>
  )
}

function PanelContent({
  children,
  className,
  leftSlot,
  onTouchMove,
}: {
  children?: ReactNode
  className?: string
  leftSlot?: ReactNode
  onTouchMove?: () => void
}) {
  return (
    <div className="flex h-full flex-1">
      {leftSlot}

      <div
        className={cn('relative flex h-full w-full overflow-hidden', className)}
        onTouchMove={onTouchMove}
      >
        <BgImage />
        <div className="z-0 h-full w-full overflow-hidden overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  )
}

function RightPanelIcon({ closePanel }: { closePanel: () => void }) {
  return (
    <div
      className="bg-primary-500/20 my-6 flex w-8 flex-shrink-0 items-center justify-center rounded-l-lg pt-6"
      onTouchMove={closePanel}
    >
      <PanelRightOpen className="text-primary-400" />
    </div>
  )
}

function BgImage() {
  const location = useLocation()
  const pathname = useMemo(() => location.pathname, [location.pathname])
  const isAdminRoute = useMemo(
    () => isRoute(RouteNames.Admin, pathname),
    [pathname],
  )
  const isOnboardingRoute = useMemo(
    () => isRoute(RouteNames.Onboarding, pathname),
    [pathname],
  )
  return (
    <div
      className="absolute bottom-0 left-0 right-0 top-0 z-0 bg-cover bg-center"
      style={{
        backgroundImage: `url(${isAdminRoute || isOnboardingRoute ? AdminArt : ProjectArt})`,
      }}
    />
  )
}
